/**
 * StaticTemplatesPage
 *
 * SEO-friendly static templates landing for bots; real users are redirected to
 * app or pricing. Includes a static content component with theme toggle.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { shouldPreventRedirectForSEO } from '../../utils/analytics/seo';
import SEOHead from '../../components/shared/SEOHead';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import { Footer } from '@/components/landing/sections/Footer/Footer';
import useDarkMode from '../../hooks/useDarkMode';

// Static content for search engines
const StaticTemplatesContent = () => {
  const isDark = useDarkMode();

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEOHead pageType="templates" />
      
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
      />
      
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
              500+ Political Campaign Templates
            </h1>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Professional political creative templates for campaigns, elections, and advocacy groups. 
              Create stunning ads, graphics, and marketing materials in minutes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Campaign Ads
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Eye-catching political advertisements for digital and print media. Perfect for social media, newspapers, and billboards.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Social media ads</li>
                <li>• Print advertisements</li>
                <li>• Billboard designs</li>
                <li>• Digital banners</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Fundraising Materials
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Professional fundraising graphics to maximize donor engagement and campaign contributions.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Donation appeals</li>
                <li>• Event invitations</li>
                <li>• Thank you cards</li>
                <li>• Goal thermometers</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Voter Outreach
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Engaging voter outreach materials to increase turnout and build campaign awareness.
              </p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <li>• Voter guides</li>
                <li>• GOTV materials</li>
                <li>• Event flyers</li>
                <li>• Volunteer recruitment</li>
              </ul>
            </div>
          </div>
          
          <div className="text-center">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Ready to Create Professional Political Content?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Join thousands of campaigns using Advocacy Lab to create high-impact political creative.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/signup"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Sign Up for Free
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                View Pricing
              </a>
            </div>
          </div>
          
          <div className="mt-16 bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-8">
              Why Choose Advocacy Lab?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">500+</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Templates</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Professional designs</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">10K+</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Campaigns</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Trust our platform</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">24/7</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Support</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Always available</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">Free</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Trial</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">No credit card</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

// Main component that handles bot detection and redirects
export default function StaticTemplatesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't redirect if this is a search engine bot
    if (shouldPreventRedirectForSEO()) {
      console.log('🤖 Bot detected on /templates - serving static content for SEO');
      return;
    }

    // For real users: redirect authenticated users to app, others to pricing/signup
    if (!loading) {
      if (user) {
        const isPremiumOrPro = user.planLevel === 'premium' || user.isEnterprise;
        if (isPremiumOrPro) {
          console.log('Authenticated premium user on /templates - redirecting to app');
          navigate('/app/', { replace: true });
        } else {
          console.log('Authenticated basic user on /templates - redirecting to pricing');
          navigate('/pricing', { replace: true });
        }
      } else {
        console.log('Unauthenticated user on /templates - redirecting to pricing');
        navigate('/pricing', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  // Always render static content (bots will see this, users will be redirected)
  return <StaticTemplatesContent />;
} 